import {
    useCallback, useEffect, useMemo, useState,
} from 'react'
import noop from 'lodash/noop'

import FormWithControls from 'app/shared-components/FormWithControls'
import useSimpleApiRequest from 'app/hooks/useSimpleApiRequest'
import StatusHandler from 'app/shared-components/StatusHandler'
import useEnumValues from 'app/hooks/useEnumValues'
import useGetContainer from 'app/shared-components/ContainerSelector/hooks/useGetContainer'
import useHasPermission from 'app/hooks/useHasPermission'
import requests from 'app/requests/Container/Damage.requests'
import Button from 'app/shared-components/Button'
import {
    DamageStatus,
} from 'app/enums/label.enums'
import useDamageTrackingRoutes from 'app/SkyNetRoutes/hooks/useDamageTrackingRoutes'
import ConfirmationDialog from 'app/shared-components/ConfirmationDialog'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'
import WarningCard from 'app/shared-components/WarningCard'

import useGetDamage from '../../hooks/useGetDamage'
import getDamageUpdateFields from './DamageUpdate.fields'
import useStyles from './DamageUpdate.styles'

export default function DamageUpdate({
    id,
}: Readonly<{ id: number }>) {
    const {
        Damages: DamagesRoutes,
    } = useDamageTrackingRoutes()
    const {
        classes,
    } = useStyles()
    const updatePermission = useHasPermission(['damage_tracking_update'])
    const {
        data,
        isDataReady,
        error,
        isError,
        invalidate,
        isFetching,
    } = useGetDamage(id)
    const [
        confirmation,
        setConfirmation,
    ] = useState<boolean>(false)

    const [
        value,
        setValue,
    ] = useState(null)

    const getAllowedValues = useEnumValues()

    const {
        data: container,
    } = useGetContainer(data?.container?.id)

    const onReopen = useCallback(({
        id: reopenedId,
    }) => {
        setConfirmation(false)
        DamagesRoutes.Update.navigate({
            params: {
                key: reopenedId,
            },
        })
    }, [DamagesRoutes])

    const onCancel = useCallback(() => {
        setValue(null)
        invalidate()
    }, [invalidate])

    const {
        mutate: reopen,
    } = useSimpleApiRequest({
        onError: noop,
        onSuccess: onReopen,
        name: 'DamageReopen',
        requestParams: requests.reopen({
            id,
        }),
        title: 'Damage Reopened',
    })

    useEffect(() => {
        if (data?.id !== value?.id) {
            setValue(data)
        }
    }, [
        data,
        value?.id,
    ])

    const handleConfirm = useCallback((positive: boolean) => {
        if (positive) {
            reopen({
                id,
            })
        }

        setConfirmation(false)
    }, [
        id,
        reopen,
    ])

    const customButtons = useMemo(() => {
        return (data?.damageStatus === DamageStatus.FIXED
            && !data?.reopened) ? [<Button
                onClick={() => {
                    setConfirmation(true)
                }}
                key="reopen"
                name="reopen"
                label="Reopen"
                secondary
            />] : []
    }, [
        data?.damageStatus,
        data?.reopened,
    ])

    const formData = useMemo(() => {
        return {
            ...data,
            ...value,
            containerModel: container?.containerType?.containerModel,
        }
    }, [
        data,
        value,
        container,
    ])

    const fields = useMemo(() => {
        return getDamageUpdateFields({
            getAllowedValues,
            editable: updatePermission,
            damageStatus: formData?.damageStatus,
            linkedPriorDamage: formData?.linkedPriorDamage,
            priorDamageLink: formData?.linkedPriorDamage?.id ? generateSkyNetLink({
                domainPath: DamagesRoutes.Update.path,
                params: {
                    key: formData?.linkedPriorDamage?.id,
                },
            }) : null,
        })
    }, [
        DamagesRoutes.Update.path,
        formData?.damageStatus,
        formData?.linkedPriorDamage,
        getAllowedValues,
        updatePermission,
    ])

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                <WarningCard>
                    <div>
                        <span className={classes.bold}>IMPORTANT</span>
                        :
                        Creating or fixing damages can result in automatic container status changes.
                    </div>
                </WarningCard>
                <FormWithControls
                    onSuccess={invalidate}
                    onCancel={onCancel}
                    value={formData}
                    setExternalValue={setValue}
                    name="Damage"
                    disabled={!updatePermission}
                    customButtons={customButtons}
                    fields={fields}
                    requestParams={requests.update({
                        id,
                    })}
                    classNames={{
                        gridWrapper: classes.gridWrapper,
                    }}
                    exists
                />
                <ConfirmationDialog
                    dialogContent={
                        [
                            'This will create a new damage entry based on current one.',
                            'Are you sure you want to reopen and continue?',
                        ]
                    }
                    open={confirmation}
                    handleClose={handleConfirm}
                    dialogTitle="Reopen Damage"
                    positiveLabel="Confirm"
                    negativeLabel="Cancel"
                />
            </>

        </StatusHandler>
    )
}
